<template>
  <div class="sp-section">
    <div class="sp-panel">
      <div class="sp-panel-header ba-accent-header">
        <h2 class="sp-panel-title">HubSpot data</h2>
        <div class="sp-controls">
          <b-form-radio-group
            v-model="measure"
            :options="measureOptions"
            buttons
            button-variant="outline-secondary"
            size="sm"
            aria-label="Show count or value"
          />
        </div>
      </div>
      <div class="sp-panel-body">
        <p class="sp-as-of mb-0">
          Data as of: <strong>{{ formatDate(lastUploadAt) || "never uploaded" }}</strong>
          <span v-if="stale" class="badge badge-warning ml-1" :title="`Over ${staleAfterDays} days old`">Stale</span>
        </p>
        <p v-if="unmappedNote" class="sp-note mb-0" role="status">
          <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>{{ unmappedNote }}
        </p>
        <hub-spot-upload-panel v-if="canUpload" class="mt-3" @uploaded="load" />
      </div>
    </div>

    <div v-if="loading && !funnel" class="sp-state">
      <b-spinner small label="Loading"></b-spinner>
      <span class="ml-2">Loading…</span>
    </div>
    <div v-else-if="error" class="sp-state sp-state-error" role="alert">
      <i class="fa fa-exclamation-triangle mr-2" aria-hidden="true"></i>{{ error }}
      <button type="button" class="btn btn-sm btn-outline-secondary ml-3" @click="load">Retry</button>
    </div>
    <template v-else-if="funnel">
      <sales-pipeline-summary :funnel="funnel" />
      <div class="sp-lost">
        <div class="sp-lost-header ba-accent-header">
          <h2 class="sp-panel-title">Why deals were lost</h2>
        </div>
        <div class="sp-panel-body">
          <p v-if="!lostReasons.total" class="mb-0">No Closed Lost deals in this period.</p>
          <div v-else class="table-responsive">
            <table class="table table-sm sp-lost-table mb-0">
              <thead>
                <tr>
                  <th scope="col">Reason</th>
                  <th scope="col" class="sp-lost-bar-col"><span class="sr-only">Relative to the largest</span></th>
                  <th scope="col" class="text-right">Deals</th>
                  <th scope="col" class="text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in lostReasons.rows" :key="row.key">
                  <th scope="row">{{ row.label }}</th>
                  <td class="sp-lost-bar-col">
                    <div class="sp-lost-track" aria-hidden="true">
                      <div class="sp-lost-bar" :style="{ width: `${row.barPercent}%` }"></div>
                    </div>
                  </td>
                  <td class="text-right">{{ formatCount(row.count) }}</td>
                  <td class="text-right">{{ formatPercent(row.share) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="sp-lost-note mb-0">
            Closed Lost deals created in this period, grouped by AI from each deal's Closed Lost Reason in HubSpot.
          </p>
        </div>
      </div>
      <div class="sp-grid">
        <analytics-chart-card
          :title="`Monthly by Stage (${measureLabel})`"
          :subtitle="stageChartSubtitle"
          :loading="loading"
          :option="monthlyChart.option"
          :table="monthlyChart.table"
        >
          <template #info>
            <p class="mb-0">
              Each month shows the deals created that month. Deals stay in that month as they move through the
              stages, including Closed, so recent months keep filling in. Existing Business deals aren't counted.
            </p>
          </template>
        </analytics-chart-card>
        <analytics-chart-card
          :title="`Cumulative by Stage (${measureLabel})`"
          :subtitle="stageChartSubtitle"
          :loading="loading"
          :option="cumulativeChart.option"
          :table="cumulativeChart.table"
        >
          <template #info>
            <p class="mb-0">
              Running totals from the first month shown, by the month each deal was created (as in Monthly by
              Stage). The last month matches the funnel summary above.
            </p>
          </template>
        </analytics-chart-card>
        <analytics-chart-card
          title="Conversion % by Month"
          :loading="loading"
          :option="rateChart.option"
          :table="rateChart.table"
        >
          <template #controls>
            <b-form-select
              v-model="rate"
              :options="rateOptions"
              size="sm"
              class="sp-rate-select"
              aria-label="Conversion rate"
            />
          </template>
          <template #info>
            <p class="mb-1">
              Each month's rate compares stage counts for the deals created that month. Always based on counts,
              whatever the Count / Value toggle says.
            </p>
            <p class="mb-0">
              Recent months read low because those deals haven't finished moving through the funnel yet. A month
              with no deals at the earlier stage shows 0%, shaded as No records.
            </p>
          </template>
        </analytics-chart-card>
      </div>
    </template>
  </div>
</template>

<script>
import { fetchSalesPipeline } from "@/services/businessAnalyticsService";
import AnalyticsChartCard from "./AnalyticsChartCard.vue";
import HubSpotUploadPanel from "./HubSpotUploadPanel.vue";
import SalesPipelineSummary from "./SalesPipelineSummary.vue";
import { formatCount } from "./chartTheme";
import {
  CONVERSION_RATES,
  MEASURE_OPTIONS,
  STALE_AFTER_DAYS,
  conversionChart,
  formatPercent,
  isStale,
  lostReasonSummary,
  runningTotals,
  stageBarChart,
} from "./salesPipeline";

const UPLOAD_PERMISSION = "businessanalytics.upload";

function errorMessage(err) {
  const status = err && err.response && err.response.status;
  if (status === 403) return "You don't have permission to view the Sales Pipeline.";
  return (
    (err && err.response && err.response.data && err.response.data.message) ||
    "Couldn't load the Sales Pipeline. Please try again."
  );
}

/**
 * Sales Pipeline section of Business Analytics: the HubSpot upload panel (with
 * `businessanalytics.upload` only), when the export was last uploaded, the Count / Value control
 * every chart in the section follows, and why deals were lost.
 */
export default {
  name: "SalesPipelineSection",
  components: {
    "hub-spot-upload-panel": HubSpotUploadPanel,
    "analytics-chart-card": AnalyticsChartCard,
    "sales-pipeline-summary": SalesPipelineSummary,
  },
  data() {
    return {
      measureOptions: MEASURE_OPTIONS,
      rateOptions: CONVERSION_RATES.map(({ value, text }) => ({ value, text })),
      staleAfterDays: STALE_AFTER_DAYS,
      measure: "count",
      rate: CONVERSION_RATES[0].value,
      funnel: null,
      loading: false,
      error: "",
      // Each load gets a number; a slower, older response never overwrites a newer one.
      requestSeq: 0,
    };
  },
  computed: {
    months() {
      return (this.funnel && this.funnel.months) || [];
    },
    measureLabel() {
      return this.measure === "value" ? "Value" : "Count";
    },
    stageChartSubtitle() {
      return this.measure === "value"
        ? "Value starts at Demo."
        : "Lead is left out so the later stages stay readable; it's in the funnel table above.";
    },
    monthlyChart() {
      return stageBarChart({ months: this.months, measure: this.measure });
    },
    cumulativeChart() {
      return stageBarChart({ months: runningTotals(this.months), measure: this.measure });
    },
    rateChart() {
      return conversionChart({ months: this.months, rate: this.rate });
    },
    canUpload() {
      return this.$store.getters["authpermission/hasAnyPermission"]([UPLOAD_PERMISSION]);
    },
    lastUploadAt() {
      return (this.funnel && this.funnel.lastUploadAt) || null;
    },
    stale() {
      return isStale(this.lastUploadAt);
    },
    lostReasons() {
      return lostReasonSummary(this.months, (this.funnel && this.funnel.lostReasonCategories) || []);
    },
    /** Deal stages the Stage Mapping doesn't cover, so those deals count at Lead only. */
    unmappedNote() {
      const unmapped = (this.funnel && this.funnel.unmappedStages) || [];
      if (!unmapped.length) return "";
      const deals = unmapped.reduce((sum, u) => sum + u.deals, 0);
      const names = unmapped.map((u) => `“${u.stage}”`).join(", ");
      return (
        `${formatCount(deals)} ${deals === 1 ? "deal is" : "deals are"} in HubSpot stages the Stage Mapping ` +
        `doesn't cover (${names}), so ${deals === 1 ? "it's" : "they're"} only counted as ${deals === 1 ? "a lead" : "leads"}.`
      );
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatCount,
    formatPercent,
    formatDate(iso) {
      return iso ? new Date(iso).toLocaleDateString("en-GB", { dateStyle: "medium" }) : "";
    },
    async load() {
      const seq = ++this.requestSeq;
      this.loading = true;
      this.error = "";
      try {
        const funnel = await fetchSalesPipeline();
        if (seq === this.requestSeq) this.funnel = funnel;
      } catch (err) {
        if (seq === this.requestSeq) this.error = errorMessage(err);
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.sp-panel {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(33, 47, 63, 0.12);
  margin-bottom: 24px;
}
.sp-panel-header {
  min-height: 52px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px 12px;
  padding: 8px 16px;
}
.sp-panel-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: inherit;
}
.sp-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.sp-rate-select {
  width: auto;
}
.sp-panel-body {
  padding: 16px;
}
.sp-as-of {
  font-size: 13px;
}
.sp-note {
  margin-top: 4px;
  font-size: 13px;
  color: #b9770e;
}
.sp-lost {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(33, 47, 63, 0.12);
  margin-bottom: 24px;
}
.sp-lost-header {
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 16px;
}
.sp-lost-table th,
.sp-lost-table td {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  vertical-align: middle;
}
.sp-lost-bar-col {
  width: 40%;
  min-width: 120px;
}
.sp-lost-track {
  height: 12px;
  border-radius: 6px;
  background: rgba(128, 128, 128, 0.12);
  overflow: hidden;
}
.sp-lost-bar {
  height: 100%;
  border-radius: 6px;
  background: #6eb644;
}
.sp-lost-note {
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.75;
}
.sp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 520px), 1fr));
  gap: 24px;
  margin-bottom: 24px;
}
.sp-state {
  min-height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  text-align: center;
  opacity: 0.8;
  margin-bottom: 24px;
}
.sp-state-error {
  color: #c0392b;
  opacity: 1;
}
</style>

<!-- Unscoped: the theme class is on <body>. Shared with the other Business Analytics cards. -->
<style src="./accentHeader.css"></style>
